import { Button, Icon, Sheet } from '@astral/ui';
import { openTelegramLink } from '@astral/telegram/webapp';
import { useT } from '../providers/I18nProvider.js';
import { telegramLink } from '../config.js';

export interface JoinChannelSheetProps {
  open: boolean;
  /** The channel the server says is required, e.g. `@AstralAtelier`. */
  channel: string;
  /** True while a re-check is in flight. */
  checking: boolean;
  /**
   * Shown when the last check could not reach Telegram. Distinct from "you are
   * not a member" — the user may well be one, so the copy says "try again"
   * rather than sending them to join something they have already joined.
   */
  unavailable: boolean;
  onCheckAgain: () => void;
  onClose: () => void;
}

/**
 * The membership gate shown on Welcome when the user is not in @AstralAtelier.
 *
 * That is the MAIN Astral channel — deliberately not the sticker-pack branding
 * token ﹙@AstralStickerBot﹚, which names the bot that makes packs. The two are
 * never interchangeable.
 *
 * Built on the same `Sheet` every other Astral modal uses, so it inherits the
 * portal, the scroll lock and the visual language rather than introducing a new
 * surface. It is deliberately NOT dismissible into the app: closing it returns
 * to Welcome, and entering requires a real server-side check.
 */
export function JoinChannelSheet({
  open,
  channel,
  checking,
  unavailable,
  onCheckAgain,
  onClose,
}: JoinChannelSheetProps): JSX.Element {
  const t = useT();
  const handle = channel.replace(/^@/, '');

  return (
    <Sheet open={open} onClose={onClose} title={t('membership.title')}>
      <div className="stack" style={{ gap: 16 }}>
        <p className="entry-panel__body" style={{ margin: 0 }}>
          {t('membership.body', { channel: `@${handle}` })}
        </p>

        {/*
          The temporary-failure case gets its own line rather than its own
          screen: the user's next action is the same button either way, and
          swapping the whole modal out would lose the join link they may still
          need.
        */}
        {unavailable && (
          <p className="packlink-bad" style={{ margin: 0, fontSize: 13 }}>
            {t('membership.unavailable')}
          </p>
        )}

        <div className="stack" style={{ gap: 10 }}>
          <Button
            fullWidth
            size="lg"
            leftIcon={<Icon name="telegram" size={18} />}
            onClick={() => openTelegramLink(telegramLink(handle))}
          >
            {t('membership.join')}
          </Button>
          <Button
            fullWidth
            size="lg"
            variant="secondary"
            loading={checking}
            disabled={checking}
            leftIcon={<Icon name="check" size={18} />}
            onClick={onCheckAgain}
          >
            {t('membership.checkAgain')}
          </Button>
        </div>
      </div>
    </Sheet>
  );
}
